import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { Paged } from 'api/common/types/Page';
import styles from './VehiclesDevicesList.module.scss';
import { useDebouncedCallback } from 'use-debounce';
import Dropdown from 'common/components/dropdown/Dropdown';
import DropdownItem from 'common/components/dropdown/DropdownItem';
import { FaEllipsisH } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import InputSearch from 'common/components/inputSearch/InputSearch';
import TrackingVehicleDevice from 'api/tracking/models/TrackingVehicleDevice';
import { VehiclesSearchCriteria } from 'api/vehicles/models/VehiclesSearchCriteria';
import TrackingService from 'api/tracking/TrackingService';

type Props = {
};

const VehiclesDevicesList: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const isFirstLoad = useRef(true);
    const filterJsonKey = 'PARAMETERS_DEVICES_FILTER';

    const history = useHistory();
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasNotificationsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_GPS_WRITE']);

    const [vehicleDevicesPage, setVehicleDevicesPage] = useState<Paged<TrackingVehicleDevice>>();
    const [criteria, setCriteria] = useState<VehiclesSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'registration_number',
        orderBy: 'asc',
    });

    const renderTableActionCell = (row: TrackingVehicleDevice) => {
        return <Dropdown
            options={
                <>
                    <DropdownItem url={`/settings/vehicleDevices/details/${row.id ?? ''}`}>
                        {t('common.details')}
                    </DropdownItem>
                    {hasNotificationsWritePolicy && <DropdownItem url={`/settings/vehicleDevices/edit/${row.id ?? ''}`}>
                        {t('common.edit')}
                    </DropdownItem>}
                </>
            }
        >
            <div>
                <FaEllipsisH />
            </div>
        </Dropdown>
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<TrackingVehicleDevice>[] = [
        {
            field: 'registrationNumber',
            name: t('vehicle_devices.list.registration_number'),
            onSearch: onTableFilter,
            searchField: 'registration_number'
        },
        {
            field: 'deviceCode',
            name: t('vehicle_devices.list.device'),
        },
        {
            field: 'status',
            name: t('vehicle_devices.list.status'),
            renderCell: row => <div>{t(('vehicle_devices.list.label_status.' + row.status) as any)}</div>,
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        }
    ];

    const getData = async () => {
        try {
            Loading.show();

            saveOrdernationFilters();

            const page = await TrackingService.getVehicleDevices({ ...criteria });
            setVehicleDevicesPage(page);

            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get vehicle devices list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            Loading.hide();
        }
    };

    const debounced = useDebouncedCallback((value: string) => {
        if (value === criteria.allIn) return;
        setCriteria({ ...criteria, page: 1, allIn: value });
    }, 500);

    useEffect(() => {
        if (isFirstLoad.current == true) {
            loadFilters()
        } else {
            getData()
        }
    }, [criteria]);

    const saveOrdernationFilters = () => {
        saveFiltersOnCache(criteria);
    }

    const saveFiltersOnCache = (f: VehiclesSearchCriteria) => {
        localStorage.setItem(filterJsonKey, JSON.stringify(f));
    }

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(filterJsonKey);
        if (cachedFilters != null) { onChangeFilters(JSON.parse(cachedFilters), true) }
        else { getData() }

        isFirstLoad.current = false;
    }

    const onChangeFilters = (f: VehiclesSearchCriteria, isOnLoad?: boolean) => {
        saveFiltersOnCache(f);
        setCriteria(prev => ({
            ...prev,
            ...f,
            page: (isOnLoad && f.page) ? f.page : 1,
        }))
    }

    return (
        <div className={styles.container}>
            <div className={styles.tabHeader}>
                <div>
                    <InputSearch defaultValue={criteria.allIn ?? undefined} onChangeValue={debounced} placeholder={t('vehicle_devices.list.search_placeholder')} ></InputSearch>
                </div>
            </div>
            {!isFirstLoad.current && <ListingTable
                columns={tableColumns}
                rows={vehicleDevicesPage?.items || []}
                onRowClick={row => history.push(`/settings/vehicleDevices/details/${row.id ?? ''}`)}
                onHref={row => `/settings/vehicleDevices/details/${row.id ?? ''}`}
                allowHover={true}
                initialSearch={{ colField: criteria.orderColumn ?? 'registration_number', isOrderAsc: (criteria.orderBy && criteria.orderBy == 'asc') ? true : false }}
            />}
            <PaginationWithInfo
                itemName={t('vehicle_devices.list.title')}
                currentPage={vehicleDevicesPage?.currentPage || 1}
                pageItems={vehicleDevicesPage?.items.length || 0}
                totalItems={vehicleDevicesPage?.totalItems || 0}
                onChange={page => setCriteria({ ...criteria, page })}
            />
        </div>
    );
};

export default VehiclesDevicesList;
